'use client';

import React, { useState } from 'react';
import Image from 'next/image';
import { useTranslations } from 'next-intl';
import { twMerge } from 'tailwind-merge';
import { fallbackLanguage, languages } from '@/integrations/i18n/settings';
import { isEuCountry } from '@/utils/country-helper';
import { useLocalization } from '@/ui/providers/localizationProvider';
import { useLegalGuaranteeContent } from '@/ui/providers/legal-guarantee-provider';
import { LegalGuaranteeModal } from './legal-guarantee-modal';

const BADGE_SRC = '/images/legal-guarantee/badge-legal-guarantee.svg';
const getBundledNoticeSrc = (language: string) => `/images/legal-guarantee/notice-${language}.svg`;

interface LegalGuaranteeBadgeProps {
  /** Renders the localized text snippet beside the badge (product overview, order confirmation). */
  withText?: boolean;
  className?: string;
}

/**
 * EU legal guarantee badge. Opens the official notice for the current language in an overlay.
 *
 * The EU market gate lives here rather than at the four call sites, so every placement inherits it.
 *
 * The notice itself is bundled per language, and the `noticeUrl` translation overrides it when set —
 * that is how marketing repoints the four placements at a Crystallize asset without a deploy. The
 * bundled copy means the label is compliant from the day this ships, rather than from the day the
 * upload happens.
 */
export function LegalGuaranteeBadge({ withText, className }: Readonly<LegalGuaranteeBadgeProps>) {
  const t = useTranslations('LegalGuarantee');
  const { language, country } = useLocalization();
  // Crystallize is the source of record; Weblate covers unauthored fields and the bundled asset
  // covers a missing notice, so the label never disappears because a CMS field is empty.
  const cmsContent = useLegalGuaranteeContent();
  const [isNoticeOpen, setIsNoticeOpen] = useState(false);

  if (!isEuCountry(country)) {
    return null;
  }

  const bundledLanguage = languages.includes(language) ? language : fallbackLanguage;
  const weblateNoticeUrl = t.has('noticeUrl') ? t('noticeUrl') : '';
  const noticeUrl =
    cmsContent?.noticeUrl || weblateNoticeUrl || getBundledNoticeSrc(bundledLanguage);

  return (
    <div className={twMerge('flex flex-wrap items-center gap-3 sm:gap-4', className)}>
      <button
        type="button"
        onClick={() => setIsNoticeOpen(true)}
        aria-haspopup="dialog"
        aria-label={cmsContent?.badgeLabel || t('badgeLabel')}
        className="shrink-0 rounded-md focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#406AFF]"
      >
        {/* Empty alt: the button already carries the accessible name via aria-label. */}
        <Image src={BADGE_SRC} alt="" width={157} height={53} className="h-10 w-auto sm:h-[53px]" />
      </button>

      {withText && <p className="max-w-xl text-sm text-neutral-70 sm:text-base">{cmsContent?.badgeText || t('badgeText')}</p>}

      <LegalGuaranteeModal
        isOpen={isNoticeOpen}
        onClose={() => setIsNoticeOpen(false)}
        noticeUrl={noticeUrl}
      />
    </div>
  );
}
